import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  CreateRawIdolDataAPI,
  DeleteRawIdolDataAPI,
  GetProductDataAPI,
  GetRawIdolDataAPI,
  UpdateRawIdolDataAPI,
} from '../../AllAPI/AdminAPI'

const RawIdolProduction = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [raws, setRaws] = useState([])
  const [products, setProducts] = useState([])
  const [searchText, setSearchText] = useState('')
  const [tableLoading, setTableLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [date, setDate] = useState(null)
  const [productId, setProductId] = useState(null)
  const [quantity, setQuantity] = useState('')

  /* ================= Delete ================= */
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchProducts()
    fetchRawIdolData()
  }, [])

  /* ================= Fetch Raw Idol Data ================= */
  const fetchRawIdolData = async () => {
    try {
      setTableLoading(true)

      const res = await axiosInstance.get(
        `${GetRawIdolDataAPI}/${selectedYear}`,
      )

      setRaws(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setTableLoading(false)
    }
  }

  /* ================= Fetch Products (Dropdown) ================= */
  const fetchProducts = async () => {
    try {
      setProductLoading(true)

      const res = await axiosInstance.get(
        `${GetProductDataAPI}/${selectedYear}`,
      )

      setProducts(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load products')
    } finally {
      setProductLoading(false)
    }
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setDate(dayjs(record.date))
      setProductId(record.product.prod_id)
      setQuantity(record.quantity)
    } else {
      setEditingId(null)
      setDate(null)
      setProductId(null)
      setQuantity('')
    }
    setIsModalOpen(true)
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!date) return toast.error('Date is required')
    if (!productId) return toast.error('Product is required')
    if (!quantity || Number(quantity) <= 0)
      return toast.error('Enter valid quantity')

    const objData = {
      date: date.format('YYYY-MM-DD'),
      prod_id: productId,
      quantity: Number(quantity),
      year: selectedYear,
    }

    try {
      setSaveLoading(true)

      if (editingId) {
        const res = await axiosInstance.put(
          `${UpdateRawIdolDataAPI}/${editingId}`,
          objData,
        )

        if (res.data === 'UPDATED') {
          toast.success('Raw Idol Updated')
        } else {
          toast.warn('Error Occurred! Try Again')
        }
      } else {
        const res = await axiosInstance.post(CreateRawIdolDataAPI, objData)

        if (res.data === 'SAVED') {
          toast.success('Raw Idol Added')
        } else {
          toast.warn('Error Occurred! Try Again')
        }
      }

      fetchRawIdolData()
      setIsModalOpen(false)
      setEditingId(null)
      setDate(null)
      setProductId(null)
      setQuantity('')
    } catch {
      toast.error('Failed to save Raw Idol')
    } finally {
      setSaveLoading(false)
    }
  }

  /* ================= Delete ================= */

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteRawIdolDataAPI}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Raw Idol deleted successfully')
        setDeleteId(null)
        fetchRawIdolData()
      }
    } catch (error) {
      toast.error('Failed to delete')
    } finally {
      setDeleteLoading(false)
    }
  }

  /* ================= Search ================= */

  const filteredRaws = raws.filter((r) => {
    const productName = r?.product?.product_name?.toLowerCase() || ''

    const dateText = r?.date || '' // "2026-01-22"

    const search = searchText.toLowerCase()

    return (
      productName.includes(search) || dateText.includes(search) //  date search like text
    )
  })

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    {
      title: 'Product Name',
      dataIndex: 'product',
      render: (product) =>
        product ? `${product.product_name} (${product.size})` : '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openModal(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => setDeleteId(record.id)}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-raw">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Raw Idol Production
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search by product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredRaws}
          rowKey="id"
          loading={tableLoading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Raw Idol' : 'Add Raw Idol'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saveLoading}
          >
            {editingId ? 'Update' : 'Save'}
          </Button>,
        ]}
      >
        <label>Date</label>
        <DatePicker value={date} onChange={setDate} style={{ width: '100%' }} />

        <label className="mt-2">Product</label>
        <Select
          value={productId}
          onChange={setProductId}
          placeholder="Select product"
          style={{ width: '100%' }}
          loading={productLoading}
        >
          {products.map((p) => (
            <Select.Option key={p.prod_id} value={p.prod_id}>
              {p.product_name} &nbsp; ({p.size})
            </Select.Option>
          ))}
        </Select>

        <label className="mt-2">Quantity</label>
        <Input
          type="number"
          min={1}
          value={quantity}
          step="1"
          onKeyDown={(e) => {
            if (e.key === '.' || e.key === ',') {
              e.preventDefault()
            }
          }}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="Enter quantity"
        />
      </Modal>

      {/* ================= DELETE ================= */}
      <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        okText="Delete"
        confirmLoading={deleteLoading}
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this Raw Idol entry?
      </Modal>
    </>
  )
}

export default RawIdolProduction
